import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import { IwtDateFilter } from '../../filters/date.filter';

import {
    IFrequency,
    TagManualInputValues,
    Dictionary,
    TagDateValueWithTransaction,
    IManualInput,
    ActualDataType,
    LogbookEntryDto,
    IStringDateRange,
    IApplicationConfiguration
} from '../../models';
import { IDateAlignmentHelperService, IGroupApprovalService, ILogbookService, IManualInputsService, IrangeCheckHelper, toDuration } from '../../services';
import { getTimestampOptions } from '../../common/helpers/timestampDataType';

export interface IManualInputsDataService {
    getRange(beginDate: Date, endDate: Date, frequency: IFrequency, maxColumns: number): angular.IPromise<{ dates: Date[]; isExceeded: boolean }>;
    getRangeSync(
        beginDate: Date,
        endDate: Date,
        frequency: IFrequency,
        config: IApplicationConfiguration,
        maxColumns: number,
        excludeEndDate?: boolean
    ): { dates: Date[]; isExceeded: boolean };
    loadData(
        dates: Date[],
        inputList: TagManualInputValues[],
        validationEnabled: boolean,
        force?: boolean,
        onChange?: (outIndex: number, inIndex: number) => void
    ): angular.IPromise<TagManualInputValues[]>;
    mergeInputListWithEntries(inputList: TagManualInputValues[], entries: LogbookEntryDto[], frequency: IFrequency): TagManualInputValues[];
}

export class ManualInputsDataService implements IManualInputsDataService {
    static serviceId = 'manualInputsDataService';

    private readonly _defaultValue: string;

    static $inject = [
        '$q',
        '$translate',
        'dateAlignmentHelperService',
        'groupApprovalService',
        'rangeCheckHelper',
        'manualInputsService',
        'wtDateFilter',
        'logbookService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$translate: angular.translate.ITranslateService,
        private _dateAlignmentHelperService: IDateAlignmentHelperService,
        private _groupApprovalService: IGroupApprovalService,
        private _rangeCheckHelper: IrangeCheckHelper,
        private _manualInputsService: IManualInputsService,
        private _wtDateFilter: IwtDateFilter,
        private _logbookService: ILogbookService
    ) {
        this._defaultValue = this._$translate.instant('Default value');
    }

    getRange(beginDate: Date, endDate: Date, frequency: IFrequency, maxColumns: number): angular.IPromise<{ dates: Date[]; isExceeded: boolean }> {
        return this._dateAlignmentHelperService.getRange(beginDate, endDate, frequency, true).then((dates) => {
            const sliced = dates.reverse().slice(0, maxColumns).reverse();
            return {
                dates: sliced,
                isExceeded: sliced.length < dates.length
            };
        });
    }

    getRangeSync(
        beginDate: Date,
        endDate: Date,
        frequency: IFrequency,
        config: IApplicationConfiguration,
        maxColumns: number,
        excludeEndDate: boolean = false
    ): { dates: Date[]; isExceeded: boolean } {
        const dates = this._dateAlignmentHelperService.getRangeSync(beginDate, endDate, frequency, config, excludeEndDate);
        const sliced = dates.reverse().slice(0, maxColumns).reverse();
        return {
            dates: sliced,
            isExceeded: sliced.length < dates.length
        };
    }

    loadData(
        dates: Date[],
        inputList: TagManualInputValues[],
        validationEnabled: boolean,
        force: boolean = false,
        onChange?: (outIndex: number, inIndex: number) => void
    ): angular.IPromise<TagManualInputValues[]> {
        const guids = inputList.map((t) => t.guid);
        const local = dates.map((d) => d).sort((a, b) => (a > b ? 1 : -1));
        const promises = [
            local.length
                ? this._manualInputsService.getManualInputs(guids, dates[0].toISOString(), moment(local.reverse()[0]).add(1, 's').toISOString(), force)
                : this._$q.when([]),
            validationEnabled ? this.readApproved(guids, dates) : this._$q.when({})
        ];
        return this._$q.all(promises).then((data) => {
            const response = data[0] as TagDateValueWithTransaction[];
            const tagsApprovedDates = data[1] as Dictionary<number[]>;
            return this.mapResults(inputList, response, tagsApprovedDates, onChange);
        });
    }

    mergeInputListWithEntries(inputList: TagManualInputValues[], entries: LogbookEntryDto[], frequency: IFrequency) {
        inputList.forEach((input) => {
            const filteredEntries = entries.filter((e) => e.identifier?.guid === input.identifierVm?.guid);
            filteredEntries
                .filter((e) => !e.range)
                .forEach((e) => {
                    e.range = { startDate: moment(e.beginDateTime), endDate: moment(e.endDateTime) };
                });
            input.values.forEach((value) => {
                const badgeVm = this._logbookService.getBadge(filteredEntries, value.date);
                const bd = moment(value.date);
                const ed = bd.clone().add(toDuration(frequency));
                const range: IStringDateRange =
                    badgeVm.entries?.length === 0 ? (badgeVm.entries[0].range as any) : { startDate: bd.toISOString(), endDate: ed.toISOString() };
                value.logbook = {
                    badge: badgeVm,
                    range
                };
            });
        });
        return inputList;
    }

    private readApproved(guids: string[], dates: Date[]) {
        const tagsApprovedDates: { [guid: string]: number[] } = {};
        const guidAndDates = { guidList: guids, dateTimeList: dates };
        return this._groupApprovalService.tagsApprovedDates(guidAndDates).then((data) => {
            Object.keys(data).forEach((k) => {
                tagsApprovedDates[k] = data[k].map((d) => moment(d).toDate().getTime());
            });
            return tagsApprovedDates;
        });
    }

    private mapResults(
        inputList: TagManualInputValues[],
        values: TagDateValueWithTransaction[],
        approvedDates: Dictionary<number[]>,
        onChange?: (rowIndex: number, colIndex: number) => void
    ): TagManualInputValues[] {
        inputList = inputList.map((d, rowIndex) => {
            const retVal = angular.copy(d);
            const tdv = values?.filter((r) => r.guid === d.guid) ?? [];
            retVal.values = retVal.values.map((v, colIndex) => {
                const date = v.date;
                const t = _.find(tdv, (s) => moment(s.timeStamp).toISOString() === moment(v.date).toISOString());
                const hasData = !!t;
                const isOutOfRange = hasData && this._rangeCheckHelper.isOutOfRange(t.value, d.min, d.max);
                const title = hasData
                    ? t.isDefaultValue
                        ? this._defaultValue
                        : this._$translate.instant('Recorded by _user_ on _date_', { user: t.transactionUser, date: this._wtDateFilter(t.transactionDate) })
                    : '';
                const result: IManualInput = {
                    date: v.date,
                    value: hasData ? t.value : null,
                    newValue: hasData ? t.value : null,
                    isApproved: !!approvedDates[d.guid] && approvedDates[d.guid].indexOf(date.getTime()) >= 0,
                    hasData,
                    isOutOfRange,
                    formattedValue: hasData ? t.comment : null,
                    title: hasData && t.logbookEntryId ? this._$translate.instant('Readonly because value is linked to a logbook entry') : title,
                    logbookEntryId: hasData ? t.logbookEntryId : null,
                    logbook: v.logbook || { badge: null, range: null }
                };
                if (d.dataType === ActualDataType.Timestamp) {
                    Object.assign(
                        result,
                        getTimestampOptions(t?.value, (value) => {
                            try {
                                result.newValue = value;
                                if (onChange) {
                                    onChange(rowIndex, colIndex);
                                }
                            } catch (e) {
                                console.error(e);
                            }
                        })
                    );
                }
                return result;
            });
            return retVal;
        });
        return inputList;
    }
}
